import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { engagementsApi, reportsApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LoadingState, ErrorState, EmptyState } from '../components/ui/states';

/** Storage keys are "<tenantId>/<engagementId>/<runId>.<ext>" — parse the run + formats out. */
function parseReportKeys(keys: string[]): Array<{ runId: string; formats: string[] }> {
  const byRun = new Map<string, string[]>();
  for (const key of keys) {
    const fileName = key.split('/').pop() ?? key;
    const [runId, ext] = fileName.split('.');
    if (!runId || !ext) continue;
    const formats = byRun.get(runId) ?? [];
    formats.push(ext);
    byRun.set(runId, formats);
  }
  return [...byRun.entries()].map(([runId, formats]) => ({ runId, formats }));
}

export function Reports() {
  const engagementsQuery = useQuery({ queryKey: ['engagements'], queryFn: () => engagementsApi.list() });
  const engagements = useMemo(() => engagementsQuery.data?.engagements ?? [], [engagementsQuery.data]);

  const reportQueries = useQueries({
    queries: engagements.map((e) => ({
      queryKey: ['reports', e.id],
      queryFn: () => reportsApi.listForEngagement(e.id),
      enabled: engagements.length > 0,
    })),
  });

  const rows = useMemo(
    () =>
      engagements.flatMap((e, i) => parseReportKeys(reportQueries[i]?.data?.keys ?? []).map((r) => ({ engagement: e, ...r }))),
    [engagements, reportQueries],
  );

  if (engagementsQuery.isLoading) return <LoadingState />;
  if (engagementsQuery.isError) return <ErrorState error={engagementsQuery.error} onRetry={() => engagementsQuery.refetch()} />;

  return (
    <div>
      <PageHeader title="Reports" description="Generated reports, ready to review or download." />
      {rows.length === 0 ? (
        <EmptyState
          icon={<FileText size={32} />}
          title="No reports generated yet"
          description="Open a completed run and generate a report — it'll show up here."
        />
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <Link key={row.runId} to={`/runs/${row.runId}`} className="block">
              <Card className="transition-shadow hover:shadow-md">
                <CardContent className="flex items-center justify-between pt-6">
                  <div>
                    <p className="font-medium text-ink">{row.engagement.name}</p>
                    <p className="text-xs text-ink-muted">Run {row.runId}</p>
                  </div>
                  <div className="flex gap-2">
                    {row.formats.map((f) => (
                      <Badge key={f} variant="brand" className="uppercase">
                        {f}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
